// File: frontend/src/hooks/usePushNotifications.ts
// Fixed React Hook for Managing Push Notifications

import { useState, useEffect, useCallback } from 'react';
import { 
  requestNotificationPermission, 
  getCurrentToken, 
  onMessageListener,
  isNotificationSupported,
  getNotificationPermission 
} from '@/lib/firebase';
import { notificationService } from '@/lib/api/notificationService';

interface PushNotificationState {
  supported: boolean;
  permission: NotificationPermission;
  token: string | null;
  loading: boolean;
  subscribed: boolean;
  error: string | null;
}

interface NotificationPayload {
  notification?: {
    title?: string;
    body?: string;
    icon?: string;
  };
  data?: {
    [key: string]: string;
  };
}

export const usePushNotifications = () => {
  const [state, setState] = useState<PushNotificationState>({
    supported: false,
    permission: 'default',
    token: null,
    loading: true,
    subscribed: false,
    error: null
  });

  // Initialize notification state
  useEffect(() => {
    const initializeNotifications = async () => {
      try {
        const supported = isNotificationSupported();
        const permission = getNotificationPermission();
        
        setState(prev => ({
          ...prev,
          supported,
          permission,
          loading: false
        }));

        // If permission is granted, try to get existing token
        if (permission === 'granted') {
          const existingToken = await getCurrentToken();
          if (existingToken) {
            setState(prev => ({
              ...prev,
              token: existingToken,
              subscribed: true
            }));
          }
        }
      } catch (error) {
        console.error('Error initializing notifications:', error);
        setState(prev => ({
          ...prev,
          loading: false,
          error: 'Failed to initialize notifications'
        }));
      }
    };

    initializeNotifications();
  }, []);

  // Listen for foreground messages
  useEffect(() => {
    const setupForegroundListener = async () => {
      try {
        await onMessageListener().then((payload: NotificationPayload) => {
          console.log('Foreground message received:', payload);
          
          // Show browser notification for foreground messages
          if (payload.notification && Notification.permission === 'granted') {
            new Notification(payload.notification.title || 'New Notification', {
              body: payload.notification.body,
              icon: payload.notification.icon || '/icon-192x192.png'
            });
          }
        });
      } catch (error) {
        console.error('Error setting up foreground listener:', error);
      }
    };

    if (state.supported && state.permission === 'granted') {
      setupForegroundListener();
    }
  }, [state.supported, state.permission]);

  // Subscribe to notifications
  const subscribe = useCallback(async (): Promise<boolean> => {
    if (!state.supported) {
      setState(prev => ({ ...prev, error: 'Notifications not supported' }));
      return false;
    }

    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      // Request permission
      const permission = await requestNotificationPermission();
      
      if (permission === 'granted') {
        // Get FCM token
        const token = await getCurrentToken();
        
        if (token) {
          // Save token to backend using the notification service
          await saveTokenToBackend(token);
          
          setState(prev => ({
            ...prev,
            permission,
            token,
            subscribed: true,
            loading: false
          }));
          
          return true;
        }
      }
      
      setState(prev => ({
        ...prev,
        permission: permission || 'default',
        loading: false,
        error: permission === 'denied' ? 'Notification permission denied' : 'Failed to get notification token'
      }));
      
      return false;
    } catch (error) {
      console.error('Error subscribing to notifications:', error);
      setState(prev => ({
        ...prev,
        loading: false,
        error: 'Failed to subscribe to notifications'
      }));
      
      return false;
    }
  }, [state.supported]);

  // Unsubscribe from notifications
  const unsubscribe = useCallback(async (): Promise<boolean> => {
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      if (state.token) {
        // Remove token from backend
        await removeTokenFromBackend(state.token);
      }

      setState(prev => ({
        ...prev,
        token: null,
        subscribed: false,
        loading: false
      }));

      return true;
    } catch (error) {
      console.error('Error unsubscribing from notifications:', error);
      setState(prev => ({
        ...prev,
        loading: false,
        error: 'Failed to unsubscribe from notifications'
      }));

      return false;
    }
  }, [state.token]);

  // Refresh token (useful for token rotation)
  const refreshToken = useCallback(async (): Promise<boolean> => {
    if (!state.supported || state.permission !== 'granted') {
      return false;
    }

    setState(prev => ({ ...prev, loading: true }));

    try {
      const newToken = await getCurrentToken();
      
      if (newToken && newToken !== state.token) {
        // Update token in backend
        await saveTokenToBackend(newToken);
        
        setState(prev => ({
          ...prev,
          token: newToken,
          loading: false
        }));
        
        return true;
      }
      
      setState(prev => ({ ...prev, loading: false }));
      return true;
    } catch (error) {
      console.error('Error refreshing token:', error);
      setState(prev => ({
        ...prev,
        loading: false,
        error: 'Failed to refresh token'
      }));
      
      return false;
    }
  }, [state.supported, state.permission, state.token]);

  return {
    ...state,
    subscribe,
    unsubscribe,
    refreshToken
  };
};

// Helper function to save token to backend using notification service
const saveTokenToBackend = async (token: string): Promise<void> => {
  try {
    const response = await notificationService.subscribeToPushNotifications({
      token,
      device_type: 'web',
      user_agent: navigator.userAgent,
      endpoint: token // FCM token acts as endpoint for web
    });
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to save token to backend');
    }
    
    console.log('FCM token saved to backend successfully');
  } catch (error) {
    console.error('Error saving FCM token to backend:', error);
    throw error;
  }
};

// Helper function to remove token from backend using notification service
const removeTokenFromBackend = async (token: string): Promise<void> => {
  try {
    const response = await notificationService.unsubscribeFromPushNotifications(token);
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to remove token from backend');
    }
    
    console.log('FCM token removed from backend successfully');
  } catch (error) {
    console.error('Error removing FCM token from backend:', error);
    throw error;
  }
};

export default usePushNotifications;